'use client';

import Image from 'next/image';
import Button from '@/components/Button';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import LogosMarquee from '@/components/LogosMarquee';
import { PLAYWRIGHT_IGNORE } from '@/lib/constants';
import bgBlur from './bgcolorblur.svg';
import { useRef } from 'react';
import { DevelocityLogo } from '@/components/Logos';
import { useAnimatedNavbarHeight } from '@/lib/hooks/useAnimatedNavbarHeight';

export const eventConfig = {
  link: 'https://dpe.org/summit2024/',
  label: 'Event',
  title: 'DPE Summit on Sept 24-25',
};

export default function Home() {
  const containerRef = useRef<HTMLDivElement>(null);
  const slideContentRef = useRef<HTMLDivElement>(null);
  const section2ContainerRef = useRef<HTMLDivElement>(null);

  const [navPlusBannerHeight] = useAnimatedNavbarHeight();

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      gsap.set(section2ContainerRef.current, {
        opacity: 1,
      });

      const tl = gsap.timeline({
        defaults: { ease: 'none', duration: 1 },
        scrollTrigger: {
          trigger: section2ContainerRef.current,
          start: () => 'clamp(top ' + `top+=${navPlusBannerHeight})`,
          scrub: 0.25,
          pin: true,
          end: () => `+=${slideContentRef.current?.offsetHeight}`,
        },
      });

      tl.from('.devlogo-anim', {
        scale: 0.25,
      });
    },
    {
      dependencies: [navPlusBannerHeight],
      scope: containerRef,
      revertOnUpdate: true,
    },
  );

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      <section ref={slideContentRef} className="px-section py-section absolute top-0 z-10 w-full bg-black text-white">
        <Image
          src={bgBlur}
          alt="decorative multicolor blur"
          className="absolute right-0 top-0 z-10 -translate-x-[33%] -translate-y-[53%]"
        />
        <Image
          src={bgBlur}
          alt="decorative multicolor blur"
          className="absolute left-0 top-0 z-10 -translate-y-[53%] translate-x-[55%]"
        />
        <div className="max-w-section relative flex flex-col items-center gap-6">
          <video
            autoPlay
            loop
            muted
            playsInline
            data-testid={PLAYWRIGHT_IGNORE}
            className="absolute -z-0 hidden w-[15rem] rounded-full md:right-[20%] md:top-[11.5rem] lg:right-[9.5rem] lg:top-[8rem] lg:block xl:right-[7.5rem] xl:top-[8.5rem]"
          >
            <source src="/assets/videos/home/hero/ai.mp4" type="video/mp4" />
          </video>
          <video
            autoPlay
            loop
            muted
            playsInline
            data-testid={PLAYWRIGHT_IGNORE}
            className="absolute -left-[2rem] -top-[2rem] -z-0 w-[6rem] rounded-full md:left-[0rem] md:top-[1rem] md:w-[8rem] lg:top-[5rem] lg:w-[12rem]"
          >
            <source src="/assets/videos/home/hero/x.mp4" type="video/mp4" />
          </video>
          <video
            autoPlay
            loop
            muted
            playsInline
            data-testid={PLAYWRIGHT_IGNORE}
            className="absolute -right-[3rem] -top-[2rem] -z-0 w-[8rem] rounded-full md:-right-[5rem] md:w-[14rem] lg:hidden"
          >
            <source src="/assets/videos/home/hero/hexnut.mp4" type="video/mp4" />
          </video>
          <div className="z-10 flex flex-col items-center justify-center gap-6 md:gap-8">
            <div className="relative flex max-w-[20rem] flex-col gap-28 pt-0 text-center md:max-w-[35rem] md:gap-4 md:pt-20 lg:max-w-[48rem] xl:max-w-[58rem]">
              <h1 className="text-h1 z-10">
                Achieve software delivery excellence in the age of{' '}
                <span className="hidden text-transparent md:inline-block">AI</span>
              </h1>
              <p className="text-body z-10 text-gray-4">
                GenAI accelerates code creation, but puts pressure on your ability to continuously deliver. Larger batch
                sizes, slower feedback loops, less predictable and comprehensible test outcomes, and rising
                infrastructure costs all threaten core CD practices. Develocity® helps teams boost speed, stability,
                and confidence—while keeping delivery costs under control.
              </p>
              <video
                autoPlay
                loop
                muted
                playsInline
                data-testid={PLAYWRIGHT_IGNORE}
                className="absolute right-[2.25rem] top-[17.75rem] -z-0 w-[15rem] rounded-full md:-right-8 md:top-[11.5rem] lg:hidden"
              >
                <source src="/assets/videos/home/hero/ai.mp4" type="video/mp4" />
              </video>
            </div>
            {/* TODO: this is an invalid button style check with design team */}
            <div className="flex gap-4 md:gap-6">
              <Button href="/develocity/trial/" variant="primary">
                Request a trial
              </Button>
              <Button
                href="/solutions/generative-ai/"
                variant="secondary"
                outline
                className="!text-gradle-blue hover:!text-white"
              >
                Learn more
              </Button>
            </div>
          </div>
          <div className="flex h-[4rem] items-center justify-center lg:h-[14rem]">
            <video
              autoPlay
              loop
              muted
              playsInline
              data-testid={PLAYWRIGHT_IGNORE}
              className="hidden aspect-square w-[20rem] rounded-4xl lg:block"
            >
              <source src="/assets/videos/home/hero/hexnut.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
        <div className="mt-3 flex w-full flex-col items-center gap-6 overflow-hidden md:mt-20">
          <p className="text-body-bold">Trusted by leading tech and global business brands</p>
          <div className="to h-0.5 w-16 rounded-full bg-gradient-to-r from-gradle-blue to-gradle-green lg:w-20"></div>
          <LogosMarquee blurToColor="#000000" />
        </div>
      </section>
      <section
        ref={section2ContainerRef}
        className="py-section px-section relative min-h-svh w-full bg-gray-2 opacity-0"
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          data-testid={PLAYWRIGHT_IGNORE}
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/assets/videos/home/hero/lights.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-30% via-[#00000080] via-[40%] to-black to-60%"></div>
        <div className="flex h-full flex-col items-center justify-center gap-32">
          <div className="max-w-section z-10 flex flex-col items-center justify-center gap-7 pt-[0rem]">
            <p className="text-eyebrow-strong text-center text-gray-4">Boost speed, stability, and confidence with</p>
            <DevelocityLogo className="devlogo-anim w-[21rem] md:w-[36rem] lg:w-[60rem]" />
          </div>
          <div className="z-10 flex flex-col items-center gap-4 pt-[1rem] text-center text-white md:pt-[5rem]">
            <h4 className="text-h3">
              Designed for developers.
              <br className="max-w-[21rem] md:block xl:hidden" /> Built for delivery at scale.
            </h4>
            <p className="text-h6 max-w-[67rem] text-gray-4">
              Develocity provides the observability and acceleration layer across the entire software lifecycle. Unlike
              typical CI tools, it shifts visibility left and enhances CI systems, ensuring fast builds, reliable tests,
              and efficient teams.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
